import { StyleSheet, Platform, Dimensions } from 'react-native';

import { spacing, radius, shadows, colors as colorsLight, colorsDark } from '../../design-system/tokens';

type ThemeColors = typeof colorsLight | typeof colorsDark;

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isSmallScreen = screenWidth < 400;

export const createAuthStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    minHeight: '100%',
  },
  inner: {
    flex: 1,
    flexGrow: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xl
  },
  headerSection: {
    width: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing['3xl'],
    marginBottom: spacing.md,
    maxWidth: 480,
    paddingHorizontal: isSmallScreen ? spacing.md : 0,
    alignSelf: 'center'
  },
  logoContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: isSmallScreen ? spacing.md : spacing.lg,
    marginTop: isSmallScreen ? 0 : spacing.xl
  },
  responsiveLogo: {
    width: Math.min(screenWidth * 0.9, 760),
    height: Math.min(screenWidth * 0.9, 760) * 0.47,
    alignSelf: 'center',
    maxHeight: screenHeight * 0.22,
    borderRadius: radius.lg
  },
  appTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: colors.onSurface,
    textAlign: 'center',
    marginTop: spacing.sm,
    marginBottom: spacing.xs
  },
  subtitle: {
    fontSize: isSmallScreen ? 13 : 22,
    fontWeight: '700',
    color: colors.onSurfaceVariant,
    textAlign: 'center',
    marginBottom: isSmallScreen ? spacing.md : spacing.lg,
    lineHeight: isSmallScreen ? 17 : 28,
    paddingHorizontal: isSmallScreen ? spacing.md : 0,
    width: '100%',
    alignSelf: 'center',
    maxWidth: 400
  },
  headerSubtitle: {
    fontSize: 20,
    fontWeight: '700',
    color: colors.onSurface,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  button: {
    width: '100%',
    paddingVertical: spacing.md,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: spacing.lg,
    backgroundColor: 'transparent',
    overflow: 'hidden'
  },
  buttonLabel: {
    fontSize: isSmallScreen ? 15 : 18,
    fontWeight: '700',
    color: colors.white,
    letterSpacing: 1,
    textAlign: 'center'
  },
  shimmer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    opacity: 0.35
  },
  shimmerGradient: {
    width: 120,
    height: '100%',
    borderRadius: radius.full
  },
  formContainer: {
    width: isSmallScreen ? '94%' : 380,
    maxWidth: isSmallScreen ? 350 : 420,
    alignSelf: 'center',
    backgroundColor: colors.surfaceContainerLow,
    borderRadius: radius.xl,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    paddingVertical: isSmallScreen ? spacing.md : spacing.xl,
    paddingHorizontal: isSmallScreen ? spacing.md : spacing.xl,
    marginTop: isSmallScreen ? spacing.md : spacing.xl,
    marginBottom: isSmallScreen ? spacing.lg : spacing.xl,
    paddingBottom: isSmallScreen ? spacing.xl : spacing.xl,
    maxHeight: isSmallScreen ? 420 : undefined,
    ...Platform.select({
      web: shadows.md,
      ios: shadows.md,
      android: shadows.md
    })
  },
  primaryButton: {
    backgroundColor: colors.primary,
    borderRadius: radius.full,
    paddingVertical: isSmallScreen ? spacing.md : spacing['2xl'],
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: isSmallScreen ? spacing.lg : spacing['2xl'],
    ...Platform.select({
      ios: {},
      android: {},
      web: {
        boxShadow: '0 4px 16px 0 rgba(79, 70, 229, 0.18)',
        transitionProperty: 'box-shadow, transform, opacity',
        transitionDuration: '200ms'
      }
    })
  },
  primaryButtonText: {
    color: colors.white,
    fontSize: isSmallScreen ? 16 : 18,
    fontWeight: '700',
    textAlign: 'center',
    letterSpacing: 0.5
  },
  buttonGradient: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    borderRadius: radius.full,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.xl,
    minHeight: 56
  },
  buttonLoader: {
    position: 'absolute',
    right: spacing.lg,
    top: '50%',
    marginTop: -10
  },
  disabledButton: {
    opacity: 0.5
  },
  socialButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceContainerLow,
    borderRadius: radius.full,
    borderWidth: 1,
    borderColor: colors.outlineVariant,
    paddingVertical: isSmallScreen ? spacing.md : spacing.lg,
    paddingHorizontal: isSmallScreen ? spacing.lg : spacing.xl,
    marginBottom: isSmallScreen ? spacing.md : spacing.xl,
    marginTop: isSmallScreen ? spacing.md : spacing.lg,
    ...(Platform.select({
      web: {
        boxShadow: '0 2px 12px 0 rgba(29, 26, 35, 0.04)',
        transitionProperty: 'box-shadow, transform, opacity',
        transitionDuration: '200ms'
      },
      ios: shadows.sm,
      android: shadows.sm
    }))
  },
  googleIcon: {
    fontSize: 20,
    color: '#4285F4',
    backgroundColor: colors.surface,
    width: 24,
    height: 24,
    textAlign: 'center',
    lineHeight: 24,
    borderRadius: 12,
    marginRight: spacing.sm
  },
  socialButtonText: {
    color: colors.onSurface,
    fontSize: 16,
    fontWeight: '600'
  },
  dividerContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: spacing.lg
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: colors.outlineVariant
  },
  dividerText: {
    fontSize: 14,
    fontWeight: '400',
    color: colors.outline,
    marginHorizontal: spacing.md
  },
  inputContainer: {
    marginBottom: spacing.lg,
    backgroundColor: 'transparent',
  },
  inputLabel: {
    fontSize: 17,
    fontWeight: '700',
    color: colors.onSurface,
    marginBottom: spacing.xs,
    letterSpacing: 0.2
  },
  switchModeLink: {
    color: colors.primary,
    fontWeight: '700',
    textAlign: 'center',
    textDecorationLine: 'underline',
    fontSize: 16,
    marginTop: spacing.md,
    marginBottom: spacing.md,
    minHeight: 44,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'color 0.18s',
      }
    })
  },
  input: {
    fontSize: 18,
    fontWeight: '400',
    color: colors.onSurface,
    backgroundColor: colors.surfaceContainerHigh,
    borderRadius: radius.lg,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.lg,
    borderWidth: 2,
    borderColor: colors.outlineVariant,
    marginBottom: 0,
    width: '100%',
    ...Platform.select({
      web: {
        outlineStyle: 'none',
        boxShadow: 'none',
        transition: 'border-color 0.16s'
      }
    })
  },
  inputError: {
    borderColor: colors.incorrect,
    backgroundColor: 'rgba(239, 68, 68, 0.06)'
  },
  inputFocused: {
    borderColor: colors.primary,
    backgroundColor: colors.surfaceContainer,
    ...Platform.select({
      web: {
        boxShadow: `0 0 0 4px rgba(79, 70, 229, 0.12), 0 2px 24px 0 rgba(29, 26, 35, 0.06)`
      }
    })
  },
  languagePickerContainer: {
    borderWidth: 2,
    borderColor: colors.outlineVariant,
    borderRadius: radius.lg,
    backgroundColor: colors.surfaceContainerHigh,
    paddingHorizontal: spacing.sm,
    ...(Platform.OS === 'ios' ? { maxHeight: 140, overflow: 'hidden' as const } : {}),
  },
  errorContainer: {
    backgroundColor: '#ffebee',
    borderRadius: radius.md,
    padding: spacing.md,
    marginBottom: spacing.md,
    borderLeftWidth: 4,
    borderLeftColor: colors.incorrect
  },
  errorText: {
    color: colors.incorrect,
    fontWeight: '600',
    fontSize: 14
  },
  footer: {
    alignItems: 'center',
    marginTop: spacing.lg,
    marginBottom: spacing.xl,
    paddingHorizontal: spacing.md
  },
  footerText: {
    fontSize: 12,
    fontWeight: '400',
    color: colors.outline,
    textAlign: 'center',
    lineHeight: 18
  }
});
